import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import { supabase } from '../../../lib/supabase';
import { adminApplicationService, supplierDocumentService } from '../../../services/supplierApplicationService';

const SupplierApplicationsTab = ({ language }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [appDocuments, setAppDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('supplier_applications')
        ?.select(`
          *,
          country:countries(name_en, name_zh),
          user:user_profiles!supplier_applications_user_id_fkey(email, full_name)
        `)
        ?.order('submitted_at', { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error('Failed to load applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredApplications = applications?.filter((app) => {
    if (filterStatus === 'all') return true;
    return app?.application_status === filterStatus;
  });

  const handleViewDetails = async (application) => {
    setSelectedApplication(application);
    setShowModal(true);
    setAppDocuments([]);
    setDocsLoading(true);

    try {
      const docs = await supplierDocumentService?.getDocumentsByApplication(application?.id);
      setAppDocuments(docs || []);
    } catch (error) {
      console.error('Failed to load application documents:', error);
    } finally {
      setDocsLoading(false);
    }
  };

  const handleApprove = async (applicationId) => {
    if (!window.confirm(language === 'en' ? 'Approve this supplier application?' : '批准此供应商申请？')) return;

    try {
      setActionLoading(true);
      // Use the new service method that handles everything
      await adminApplicationService.approveApplication(applicationId);

      await loadApplications();
      alert(language === 'en' ? 'Application approved successfully' : '申请已成功批准');
    } catch (error) {
      console.error('Failed to approve:', error);
      alert(language === 'en' ? 'Failed to approve application' : '批准失败');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (applicationId) => {
    const reason = window.prompt(language === 'en' ? 'Rejection reason:' : '拒绝原因：');
    if (!reason) return;

    try {
      setActionLoading(true);
      const { error } = await supabase
        ?.from('supplier_applications')
        ?.update({
          application_status: 'rejected',
          rejection_reason: reason,
          reviewed_at: new Date()?.toISOString()
        })
        ?.eq('id', applicationId);

      if (error) throw error;
      await loadApplications();
      alert(language === 'en' ? 'Application rejected' : '申请已拒绝');
    } catch (error) {
      console.error('Failed to reject:', error);
      alert(language === 'en' ? 'Failed to reject application' : '拒绝失败');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: language === 'en' ? 'Pending' : '待处理' },
      documents_pending: { color: 'bg-blue-100 text-blue-800', label: language === 'en' ? 'Docs Pending' : '文档待处理' },
      under_review: { color: 'bg-purple-100 text-purple-800', label: language === 'en' ? 'Under Review' : '审核中' },
      approved: { color: 'bg-green-100 text-green-800', label: language === 'en' ? 'Approved' : '已批准' },
      rejected: { color: 'bg-red-100 text-red-800', label: language === 'en' ? 'Rejected' : '已拒绝' }
    };
    const config = statusConfig?.[status] || statusConfig?.pending;
    return <span className={`px-2 py-1 rounded-md text-xs font-medium ${config?.color}`}>{config?.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">
          {language === 'en' ? 'Supplier Applications' : '供应商申请'}
        </h2>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e?.target?.value)}
          className="px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">{language === 'en' ? 'All Status' : '所有状态'}</option>
          <option value="pending">{language === 'en' ? 'Pending' : '待处理'}</option>
          <option value="documents_pending">{language === 'en' ? 'Docs Pending' : '文档待处理'}</option>
          <option value="under_review">{language === 'en' ? 'Under Review' : '审核中'}</option>
          <option value="approved">{language === 'en' ? 'Approved' : '已批准'}</option>
          <option value="rejected">{language === 'en' ? 'Rejected' : '已拒绝'}</option>
        </select>
      </div>
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'Business Name' : '企业名称'}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'Contact' : '联系人'}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'Country' : '国家'}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'Type' : '类型'}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'Status' : '状态'}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'Submitted' : '提交时间'}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{language === 'en' ? 'Actions' : '操作'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredApplications?.map((app) => (
                <tr key={app?.id} className="hover:bg-muted/50 transition-smooth">
                  <td className="px-4 py-3 text-sm text-foreground font-medium">{app?.business_name}</td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    <div>{app?.contact_person}</div>
                    <div className="text-xs text-muted-foreground">{app?.email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{language === 'en' ? app?.country?.name_en : app?.country?.name_zh}</td>
                  <td className="px-4 py-3 text-sm text-foreground capitalize">{app?.supplier_type?.replace('_', ' ')}</td>
                  <td className="px-4 py-3">{getStatusBadge(app?.application_status)}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{new Date(app?.submitted_at)?.toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewDetails(app)}
                        className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                        aria-label="View details"
                      >
                        <Icon name="Eye" size={16} color="var(--color-primary)" />
                      </button>
                      {(app?.application_status === 'pending' || app?.application_status === 'under_review') && (
                        <>
                          <button
                            onClick={() => handleApprove(app?.id)}
                            className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                            aria-label="Approve"
                            disabled={actionLoading}
                          >
                            <Icon name="Check" size={16} color="var(--color-success)" />
                          </button>
                          <button
                            onClick={() => handleReject(app?.id)}
                            className="p-1.5 rounded-md hover:bg-muted transition-smooth"
                            aria-label="Reject"
                            disabled={actionLoading}
                          >
                            <Icon name="X" size={16} color="var(--color-error)" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {filteredApplications?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Inbox" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground">{language === 'en' ? 'No applications found' : '未找到申请'}</p>
        </div>
      )}
      {showModal && selectedApplication && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm" onClick={() => setShowModal(false)}>
          <div className="bg-card border border-border rounded-lg shadow-modal max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-foreground">{language === 'en' ? 'Application Details' : '申请详情'}</h3>
                <button onClick={() => setShowModal(false)} className="p-2 rounded-md hover:bg-muted transition-smooth">
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{language === 'en' ? 'Business Name' : '企业名称'}</label>
                  <p className="text-foreground">{selectedApplication?.business_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{language === 'en' ? 'Contact Person' : '联系人'}</label>
                  <p className="text-foreground">{selectedApplication?.contact_person}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{language === 'en' ? 'Email' : '电子邮件'}</label>
                  <p className="text-foreground">{selectedApplication?.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{language === 'en' ? 'Phone' : '电话'}</label>
                  <p className="text-foreground">{selectedApplication?.phone}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{language === 'en' ? 'Years in Operation' : '运营年限'}</label>
                  <p className="text-foreground">{selectedApplication?.years_in_operation}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{language === 'en' ? 'Registration Number' : '注册号'}</label>
                  <p className="text-foreground">{selectedApplication?.business_registration_number}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{language === 'en' ? 'Physical Address' : '实际地址'}</label>
                <p className="text-foreground">{selectedApplication?.physical_address}</p>
              </div>

              {/* Documents Section */}
              <div className="pt-4 border-t border-border">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="FileText" size={16} />
                  {language === 'en' ? 'Submitted Documents' : '提交的文件'}
                </h4>

                {docsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    {language === 'en' ? 'Loading documents...' : '正在加载文件...'}
                  </div>
                ) : appDocuments?.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {appDocuments?.map(doc => (
                      <div key={doc?.id} className="p-3 border border-border rounded-md bg-muted/20 flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground truncate">{doc?.documentName}</p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {doc?.documentType?.replace(/_/g, ' ')}
                          </p>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              const url = await supplierDocumentService?.getDocumentUrl(doc?.fileUrl);
                              window.open(url, '_blank');
                            } catch (err) {
                              alert(language === 'en' ? 'Error opening file' : '打开文件时出错');
                            }
                          }}
                          className="ml-3 p-2 text-primary hover:bg-primary/10 rounded-md transition-smooth flex items-center gap-1 text-xs font-semibold"
                        >
                          <Icon name="ExternalLink" size={14} />
                          {language === 'en' ? 'View' : '查看'}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic py-2">
                    {language === 'en' ? 'No documents submitted for this application.' : '此申请未提交任何文件。'}
                  </p>
                )}
              </div>

              {selectedApplication?.admin_notes && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{language === 'en' ? 'Admin Notes' : '管理员备注'}</label>
                  <p className="text-foreground">{selectedApplication?.admin_notes}</p>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-border bg-muted/30">
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-smooth rounded-md"
                >
                  {language === 'en' ? 'Close' : '关闭'}
                </button>
                {(selectedApplication?.application_status === 'pending' || selectedApplication?.application_status === 'under_review') && (
                  <>
                    <button
                      onClick={() => {
                        handleReject(selectedApplication?.id);
                        setShowModal(false);
                      }}
                      className="px-4 py-2 text-sm font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-smooth rounded-md flex items-center gap-2"
                      disabled={actionLoading}
                    >
                      <Icon name="X" size={16} />
                      {language === 'en' ? 'Reject' : '拒绝'}
                    </button>
                    <button
                      onClick={() => {
                        handleApprove(selectedApplication?.id);
                        setShowModal(false);
                      }}
                      className="px-4 py-2 text-sm font-medium bg-green-50 text-green-600 hover:bg-green-100 transition-smooth rounded-md flex items-center gap-2"
                      disabled={actionLoading}
                    >
                      <Icon name="Check" size={16} />
                      {language === 'en' ? 'Approve' : '批准'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierApplicationsTab;